import { SortingState } from "@tanstack/react-table"

import getObjectCardsViewConfigStorageKey from "@utils/getObjectCardsViewConfigStorageKey"

import useObjectsView from "@organisms/ObjectsView/ObjectsView.context"
import { ObjectsData } from "@organisms/ObjectsView/ObjectsView.types"

import { CardsViewContext } from "./CardsView.context"
import { CardsViewContextValues, CardsViewProviderProps } from "./CardsView.types"
import useCardsViewConfig from "./hooks/useCardsViewConfig"

// Cards view machinery only (the attributes and sort fields the object offers, and the persisted cards
// view settings). The object list itself comes from the surrounding ObjectsView.Provider.
export default function CardsViewProvider<T = ObjectsData>(props: CardsViewProviderProps<T>) {
    const { children, attributes, sortOptions } = props

    const { objectType, objectTypeVariation, activePhase, activeTab, sorting, onSortingChange } = useObjectsView()

    const storageKey = getObjectCardsViewConfigStorageKey({
        objectType,
        objectTypeVariation,
        phase: activePhase,
        tab: activeTab?.title ?? "",
    })

    const { currentCardsViewConfig, updateCardsViewConfig } = useCardsViewConfig(storageKey)

    // The config records what the user turned off; an attribute it doesn't know about is visible.
    const attributeVisibility = currentCardsViewConfig?.attributeVisibility ?? {}
    const isAttributeVisible = (attributeName: Extract<keyof T, string>) => attributeVisibility[attributeName] ?? true

    const displayedAttributes = attributes
        .filter((attribute) => isAttributeVisible(attribute.name))
        .map((attribute) => attribute.name)

    const toggleAttribute = (attributeName: Extract<keyof T, string>) => {
        updateCardsViewConfig("attributeVisibility", {
            ...attributeVisibility,
            [attributeName]: !isAttributeVisible(attributeName),
        })
    }

    // The surrounding list resolves its ordering from this view's saved config while the cards view is
    // active, so sort changes go through the list rather than straight into the config.
    const activeSorting = sorting[0]
    const activeSortOption = sortOptions.find((sortOption) => sortOption.name === activeSorting?.id)

    const setSorting = (nextSorting: SortingState) => {
        onSortingChange(nextSorting)
    }

    const setSortField = (fieldName: Extract<keyof T, string>) => {
        setSorting([{ id: fieldName, desc: activeSorting?.desc ?? false }])
    }

    const setCurrentSortToAscending = () => {
        if (activeSorting) {
            setSorting([{ id: activeSorting.id, desc: false }])
        }
    }

    const setCurrentSortToDescending = () => {
        if (activeSorting) {
            setSorting([{ id: activeSorting.id, desc: true }])
        }
    }

    const contextValue: CardsViewContextValues<T> = {
        attributes,
        sortOptions,
        displayedAttributes,
        toggleAttribute,
        activeSorting,
        activeSortOption,
        setSortField,
        setCurrentSortToAscending,
        setCurrentSortToDescending,
    }

    return <CardsViewContext.Provider value={contextValue}>{children}</CardsViewContext.Provider>
}
